// src/components/CardEditor/CardErrorModal.tsx
// Mini-painel de erro reportado, aberto direto do EditorPage — evita ter que
// sair pro painel administrativo pra ver o que foi reportado sobre este card
// específico. Reaproveita as mesmas rotas de PATCH status/DELETE já usadas
// pelo painel admin (server/routes/errorRoutes.js).

import React, { useState } from 'react';
import { X, CheckCircle, XCircle, Trash2, AlertCircle } from 'lucide-react';
import './CardErrorModal.css';

export interface CardErrorReport {
    _id: string;
    cardId: string;
    type: string;
    description: string;
    status: 'pending' | 'accepted' | 'denied';
    createdAt: string;
    userId?: { username?: string } | null;
}

interface CardErrorModalProps {
    reports: CardErrorReport[];
    isAdmin: boolean;
    onClose: () => void;
    onUpdateStatus: (errorId: string, status: 'accepted' | 'denied') => Promise<void> | void;
    onDelete: (errorId: string) => Promise<void> | void;
}

function getStatusLabel(status: string) {
    if (status === 'pending') return 'Pendente';
    if (status === 'accepted') return 'Aceito';
    if (status === 'denied') return 'Negado';
    return status;
}

export const CardErrorModal: React.FC<CardErrorModalProps> = ({ reports, isAdmin, onClose, onUpdateStatus, onDelete }) => {
    const [busyId, setBusyId] = useState<string | null>(null);

    const run = async (id: string, fn: () => Promise<void> | void) => {
        setBusyId(id);
        try {
            await fn();
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="cem-overlay" onClick={onClose}>
            <div className="cem-modal" onClick={e => e.stopPropagation()}>
                <div className="cem-header">
                    <h3 className="cem-title">
                        <AlertCircle size={16} />
                        Erro{reports.length > 1 ? 's' : ''} reportado{reports.length > 1 ? 's' : ''} neste card
                    </h3>
                    <button className="cem-close-btn" onClick={onClose}><X size={16} /></button>
                </div>

                <div className="cem-body">
                    {reports.map(report => (
                        <div key={report._id} className={`cem-report cem-report--${report.status}`}>
                            <div className="cem-report-top">
                                <strong className="cem-report-type">{report.type}</strong>
                                <span className={`cem-status-badge cem-status-badge--${report.status}`}>
                                    {getStatusLabel(report.status)}
                                </span>
                            </div>
                            <p className="cem-report-desc">{report.description}</p>
                            <div className="cem-report-meta">
                                <span>Reportado por: {report.userId?.username || 'Usuário removido'}</span>
                                <span className="cem-meta-sep">·</span>
                                <span>{new Date(report.createdAt).toLocaleDateString('pt-BR')}</span>
                            </div>

                            {isAdmin && (
                                <div className="cem-report-actions">
                                    {report.status === 'pending' && (
                                        <>
                                            <button
                                                className="cem-btn cem-btn-accept"
                                                disabled={busyId === report._id}
                                                onClick={() => run(report._id, () => onUpdateStatus(report._id, 'accepted'))}
                                            >
                                                <CheckCircle size={14} /> Aceitar
                                            </button>
                                            <button
                                                className="cem-btn cem-btn-deny"
                                                disabled={busyId === report._id}
                                                onClick={() => run(report._id, () => onUpdateStatus(report._id, 'denied'))}
                                            >
                                                <XCircle size={14} /> Negar
                                            </button>
                                        </>
                                    )}
                                    <button
                                        className="cem-btn cem-btn-delete"
                                        disabled={busyId === report._id}
                                        onClick={() => run(report._id, () => onDelete(report._id))}
                                        title="Excluir report"
                                    >
                                        <Trash2 size={14} /> Excluir
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
